import { MACHINE_ERA, STOP_POINTS, eraRules } from '@continuum/show-core';
import type { Lang } from './text.js';

/**
 * The words of an era's round (Marek, 2026-09-28): the call to play, the ship charged, each stop's
 * new roles shown only while held, and the saboteur's own game. Plain words, one idea a line, English
 * and bokmål with the same keys (the type holds them to it).
 *
 * The saboteur's game is their game of the stop (Marek, 2026-10-06): it has an ordinary-sounding name,
 * so a list with it in looks like anyone's list, and three versions (games/Hack.tsx), each with its own
 * line to play it by. Its alarm goes off once everyone's games are done.
 */
export interface RoundCopy {
  playEyebrow: (year: string) => string;
  playTitle: string;
  playLine: string;
  /** The guest's games this era: their own, and any they stand in for. */
  playGames: (names: readonly string[]) => string;
  playGo: string;
  later: string;

  chargedTitle: string;
  chargedLine: string;
  chargedSub: string;
  ok: string;

  /** Before each jump, privately: in the next stop's saboteur draw, or not (Marek, 2026-10-01). */
  drawEyebrow: string;
  drawTitle: string;
  drawLine: string;
  drawYes: string;
  drawNo: string;
  /** Beside the answer the guest has now. */
  drawNow: string;
  drawNote: string;

  /** Each era's role step (2026-10-09): everyone looks at once, so nobody's phone gives anything away. */
  roleStepTitle: string;
  roleStepLine: string;
  roleStepOpen: string;
  roleStepSeen: string;
  roleStepAgain: string;
  /** The stop's meeting, before its vote: the table talks it over. */
  talkEyebrow: string;
  talkTitle: string;
  talkLine: string;

  roleEyebrow: (year: string) => string;
  roleNew: string;
  holdToSee: string;
  holdHint: string;
  agentTitle: string;
  agentTask: (chapter: number) => string[];
  crewTitle: string;
  crewTask: string[];
  gotIt: string;
  heldFirst: string;

  hackTitle: string;
  /** Under the title whichever version is dealt, so it fits all three. */
  hackInstruction: string;
  /** Each version in one line (games/Hack.tsx): the wires, the circuit, the lock. */
  hackHow: readonly [string, string, string];
  hackSecret: string;
  hackStart: string;
  hackWires: string;
  hackTrace: string;
  hackTraceWrong: string;
  hackDial: string;
  hackDialSet: string;
  /** SET pressed off the number: the one to turn to. */
  hackDialFirst: (n: string) => string;
  hackCode: string;
  hackWrong: string;
  hackSending: string;
  hackDone: string;
  /** The stop's games are over (its alarms, the vote): too late to play it. */
  hackBusy: string;
  hackNone: string;
  hackNotNow: string;
  hackClose: string;
}

const EN: RoundCopy = {
  playEyebrow: (year) => `${year} · THE GAMES`,
  playTitle: 'PLAY NOW',
  playLine: 'Every game done charges the ship.',
  playGames: (names) => (names.length === 1 ? `Your game: ${names[0]}` : `Your games: ${names.join(' · ')}`),
  playGo: 'PLAY NOW ▸',
  later: 'Later',

  chargedTitle: 'SHIP CHARGED',
  chargedLine: 'ALL CREW DONE',
  chargedSub: 'Next: the alarms. Watch the table.',
  ok: 'OK',

  drawEyebrow: 'BEFORE THE JUMP',
  drawTitle: 'NEXT STOP: SABOTEUR?',
  drawLine: 'New saboteurs are drawn as we jump. Want to be in the draw?',
  drawYes: 'YES, PUT ME IN',
  drawNo: 'NO, NOT THIS TIME',
  drawNow: 'NOW',
  drawNote: 'Only you and the captain see your answer.',

  roleStepTitle: 'YOUR ROLE',
  roleStepLine: 'Saboteur or crew? Everyone looks now, at the same time. Keep it to yourself.',
  roleStepOpen: 'SEE YOUR ROLE',
  roleStepSeen: 'Seen. Keep it to yourself.',
  roleStepAgain: 'SEE IT AGAIN',
  talkEyebrow: 'MEETING',
  talkTitle: 'WHO IS THE SABOTEUR?',
  talkLine: 'Talk it over at the table. The vote opens next, on your phone.',

  roleEyebrow: (year) => `YOUR ROLE · ${year}`,
  roleNew: 'New roles for this stop. Only you can see yours, so keep your screen to yourself.',
  holdToSee: 'HOLD TO SEE YOUR ROLE',
  holdHint: 'Keep your finger on it. Let go to hide it.',
  agentTitle: 'YOU ARE THE SABOTEUR',
  agentTask: (chapter) => [
    'Your task: damage the ship without getting caught.',
    'Your game this stop is REWIRE THE CORE. Play it while the others play theirs.',
    'Its alarm goes off near you once everyone’s games are done. Not fixed in time, it damages the ship.',
    'Then everyone votes. Act like crew: help fix the alarms, and vote like everyone else.',
    `Not caught: +${STOP_POINTS.ESCAPED}. Each damage your alarm does: +${STOP_POINTS.DAMAGE}. Caught: −${-STOP_POINTS.CAUGHT}.`,
    ...(chapter === MACHINE_ERA
      ? [`In The Boom the machines help you: FIX goes to ${eraRules(MACHINE_ERA).stabiliseCrew} people, and ${eraRules(MACHINE_ERA).stabiliseCrew - 1} of them must solve it.`]
      : []),
  ],
  crewTitle: 'YOU ARE CREW',
  crewTask: [
    'Play your game: every game done charges the ship.',
    'Then the alarms go off. A white circle at your place and FIX MISSION on your phone: solve the quick puzzle.',
    `Then everyone votes for a saboteur. A right vote: +${STOP_POINTS.RIGHT_VOTE}.`,
  ],
  gotIt: 'GOT IT',
  heldFirst: 'Hold the button first.',

  hackTitle: 'REWIRE THE CORE',
  hackInstruction: 'Solve the puzzle, quick and quiet.',
  hackHow: [
    'Match the wires, then type the code.',
    'Join the dots from 1 to 6, then type the code.',
    'Turn the dial to three numbers to open the lock.',
  ],
  hackSecret: 'Your game this stop. Solve it, and your alarm goes off once everyone’s games are done.',
  hackStart: 'START',
  hackWires: 'Tap a wire, then the socket with the same colour and shape.',
  hackTrace: 'Tap the dots in order, from 1 to 6.',
  hackTraceWrong: 'Wrong dot. Start again from 1.',
  hackDial: 'Turn the dial to each number, then press SET.',
  hackDialSet: 'SET',
  hackDialFirst: (n) => `Turn to ${n} first.`,
  hackCode: 'Now type this code',
  hackWrong: 'Not that one. Try again.',
  hackSending: 'Sending…',
  hackDone: 'Done. Your alarm goes off once everyone’s games are done. Act normal.',
  hackBusy: 'The games are over for this stop.',
  hackNone: 'Nothing more to do at this stop.',
  hackNotNow: 'Only during the games.',
  hackClose: 'Close',
};

const NO: RoundCopy = {
  playEyebrow: (year) => `${year} · SPILLENE`,
  playTitle: 'SPILL NÅ',
  playLine: 'Hvert spill som blir ferdig, lader skipet.',
  playGames: (names) => (names.length === 1 ? `Ditt spill: ${names[0]}` : `Dine spill: ${names.join(' · ')}`),
  playGo: 'SPILL NÅ ▸',
  later: 'Senere',

  chargedTitle: 'SKIPET ER LADET',
  chargedLine: 'HELE MANNSKAPET ER FERDIG',
  chargedSub: 'Nå kommer alarmene. Følg med på bordet.',
  ok: 'OK',

  drawEyebrow: 'FØR HOPPET',
  drawTitle: 'NESTE STOPP: SABOTØR?',
  drawLine: 'Nye sabotører trekkes når vi hopper. Vil du være med i trekningen?',
  drawYes: 'JA, TA MEG MED',
  drawNo: 'NEI, IKKE NÅ',
  drawNow: 'NÅ',
  drawNote: 'Bare du og kapteinen ser svaret ditt.',

  roleStepTitle: 'DIN ROLLE',
  roleStepLine: 'Sabotør eller mannskap? Alle ser nå, samtidig. Hold det for deg selv.',
  roleStepOpen: 'SE ROLLEN DIN',
  roleStepSeen: 'Sett. Hold det for deg selv.',
  roleStepAgain: 'SE DEN IGJEN',
  talkEyebrow: 'MØTE',
  talkTitle: 'HVEM ER SABOTØREN?',
  talkLine: 'Snakk sammen rundt bordet. Avstemningen kommer straks, på telefonen.',

  roleEyebrow: (year) => `DIN ROLLE · ${year}`,
  roleNew: 'Nye roller for dette stoppet. Bare du ser din, så hold skjermen for deg selv.',
  holdToSee: 'HOLD FOR Å SE ROLLEN DIN',
  holdHint: 'Hold fingeren på. Slipp for å skjule den.',
  agentTitle: 'DU ER SABOTØREN',
  agentTask: (chapter) => [
    'Oppgaven din: skad skipet uten å bli tatt.',
    'Spillet ditt på dette stoppet er KOBLE OM KJERNEN. Spill det mens de andre spiller sine.',
    'Alarmen går nær deg når alle spillene er ferdige. Blir den ikke fikset i tide, får skipet skade.',
    'Så stemmer alle. Oppfør deg som mannskapet: hjelp til med å fikse alarmene, og stem som alle andre.',
    `Ikke tatt: +${STOP_POINTS.ESCAPED}. Hver skade alarmen din gjør: +${STOP_POINTS.DAMAGE}. Tatt: −${-STOP_POINTS.CAUGHT}.`,
    ...(chapter === MACHINE_ERA
      ? [`I Boomen hjelper maskinene deg: FIKS går til ${eraRules(MACHINE_ERA).stabiliseCrew} personer, og ${eraRules(MACHINE_ERA).stabiliseCrew - 1} av dem må løse det.`]
      : []),
  ],
  crewTitle: 'DU ER MANNSKAP',
  crewTask: [
    'Spill spillet ditt: hvert spill som blir ferdig, lader skipet.',
    'Så går alarmene. En hvit sirkel på plassen din og FIKSEOPPDRAG på telefonen: løs det korte puslespillet.',
    `Så stemmer alle på en sabotør. Riktig stemme: +${STOP_POINTS.RIGHT_VOTE}.`,
  ],
  gotIt: 'SKJØNNER',
  heldFirst: 'Hold knappen først.',

  hackTitle: 'KOBLE OM KJERNEN',
  hackInstruction: 'Løs puslespillet, raskt og stille.',
  hackHow: [
    'Koble ledningene, og skriv inn koden.',
    'Koble prikkene fra 1 til 6, og skriv inn koden.',
    'Drei skiven til tre tall for å åpne låsen.',
  ],
  hackSecret: 'Spillet ditt på dette stoppet. Løser du det, går alarmen din når alle spillene er ferdige.',
  hackStart: 'START',
  hackWires: 'Trykk på en ledning, så på kontakten med samme farge og form.',
  hackTrace: 'Trykk på prikkene i rekkefølge, fra 1 til 6.',
  hackTraceWrong: 'Feil prikk. Begynn på nytt fra 1.',
  hackDial: 'Drei skiven til hvert tall, og trykk VELG.',
  hackDialSet: 'VELG',
  hackDialFirst: (n) => `Drei til ${n} først.`,
  hackCode: 'Skriv inn denne koden',
  hackWrong: 'Ikke den. Prøv igjen.',
  hackSending: 'Sender …',
  hackDone: 'Ferdig. Alarmen din går når alle spillene er ferdige. Oppfør deg normalt.',
  hackBusy: 'Spillene er over for dette stoppet.',
  hackNone: 'Ikke mer å gjøre på dette stoppet.',
  hackNotNow: 'Bare mens spillene pågår.',
  hackClose: 'Lukk',
};

export const ROUND_COPY: Record<Lang, RoundCopy> = { en: EN, no: NO };
